// Per spec/blocks.md §big3-signature-events (P-037.24 / D-132). Banks's
// call from the P-037.23 close session chat: take event-card-group's
// post-D-131 shape, fork into a new Block specifically for the dual
// composition of Big 3 Meetings + Signature Events. Two baked
// `SubSectionHeader` calls (one per arm) frame the Block, the outer
// table consumes `ctx.tintColor` for the per-Header-Style 10 % tint
// (per D-131 item 1), and the Big 3 arm renders via the new
// `big3EventCard()` Component which substitutes a Canvas-composited
// 190 × 110 PNG (event image + Big 3 logo overlay + navy bar baked in)
// for EventCard's separate-navy-bar + image-cell shape.
//
// **Intentional exception to the "section-heading lives in its own
// Block" pattern (D-126):** the dual-section composition IS this
// Block's identity. Replacing the two baked SubSectionHeader calls
// with two standalone `sub-section-heading` Blocks would erase that
// identity and let staff break the composition by reordering. The
// section-heading Block + sub-section-heading Block remain available
// for any OTHER content that wants a heading; this Block is opinionated
// about its own structure.
//
// **Composite-image flow.** Each `big3Events[i]` carries up to two
// source URLs (`imageUrl`, `big3LogoUrl`) plus a derived
// `compositeImageUrl` field (brand `'derived'` — invisible in the
// form; populated by the composite-on-change orchestrator after staff
// edits either source). The Component reads `compositeImageUrl` if
// set, else falls back to the regular EventCard shape with no logo.
// The render pipeline never composes — pure function of the schema.
//
// **Signature Events arm** reuses the existing `eventCard()` Component
// unmodified. No logo overlay; same 10 px navy bar + 190 × 100 image
// shape as event-card-group.
//
// Auto-hide fires when BOTH arrays are empty (orchestrator-level skip
// per the shared `*.length === 0` convention).

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { eventCard, type EventCardProps } from '../components/event-card'
import { big3EventCard, type Big3EventCardProps } from '../components/big3-event-card'
import { subSectionHeader } from '../components/sub-section-header'
import { gridLayout, pickDefined } from '../components/helpers'

// P-037.31 fourth post-Phase-Close §A — `'Learn More'` added per Banks's audit.
const CTA_LABEL_ENUM = ['Register Now', 'Add to Waitlist', 'Learn More', 'Save the Date'] as const
type CtaLabel = (typeof CTA_LABEL_ENUM)[number]

// Common per-event fields used by the Signature Events arm. The Big 3
// arm declares its own schema (rather than extending this one) so the
// per-item fields can interleave: `big3LogoUrl` sits BETWEEN `imageUrl`
// and `ctaLabel/ctaUrl` so the staff form groups image-shaped fields
// together (P-037.25 D-133).
const signatureEventItemSchema = z.object({
  title: z.string().min(1),
  dateText: z.string().min(1),
  location: z.string().min(1).optional(),
  imageUrl: z.string().url().describe('imageUrl').optional(),
  ctaLabel: z.enum(CTA_LABEL_ENUM).optional(),
  ctaUrl: z.string().url().optional(),
})

// P-037.25 D-133 — declared standalone (not via
// `signatureEventItemSchema.extend(...)`) so `big3LogoUrl` can land
// between `imageUrl` and `ctaLabel` rather than being appended after
// the parent's CTA fields. Field order drives form-field order via
// the schema-to-fields walker (tech-arch §7).
const big3EventItemSchema = z.object({
  title: z.string().min(1),
  dateText: z.string().min(1),
  location: z.string().min(1).optional(),
  imageUrl: z.string().url().describe('imageUrl').optional(),
  big3LogoUrl: z.string().url().describe('imageUrl').optional(),
  ctaLabel: z.enum(CTA_LABEL_ENUM).optional(),
  ctaUrl: z.string().url().optional(),
  // Derived: populated by the composite-on-change orchestrator (see
  // src/app/form/effects/big3-composite-orchestrator.tsx). The
  // `'derived'` brand hides this field from the form generator —
  // staff never sees or edits it. The render reads it from the
  // persisted instance state; pre-orchestrator (e.g. a newly added
  // card) the field is undefined and Big3EventCard renders the
  // fallback arm (regular EventCard shape with no logo).
  compositeImageUrl: z.string().url().describe('derived').optional(),
})

type SignatureEventItem = z.infer<typeof signatureEventItemSchema>
type Big3EventItem = z.infer<typeof big3EventItemSchema>

const optionsSchema = z.object({})

const inputsSchema = z.object({
  big3Events: z.array(big3EventItemSchema).max(3),
  signatureEvents: z.array(signatureEventItemSchema).max(6),
})

function signatureEventToProps(item: SignatureEventItem, linkColor: string): EventCardProps {
  const baseProps = {
    title: item.title,
    dateText: item.dateText,
    linkColor,
    ...pickDefined({ location: item.location, imageUrl: item.imageUrl }),
  }
  if (item.ctaLabel === undefined) {
    return baseProps
  }
  // Mirror event-card-group's eventToProps dispatch (D-130): Save the
  // Date → text-only (ignore ctaUrl); Register Now / Add to Waitlist
  // + ctaUrl → link arm; label set without URL → text fallback.
  const isLinkArm = item.ctaLabel !== 'Save the Date' && item.ctaUrl !== undefined
  return isLinkArm
    ? { ...baseProps, ctaLabel: item.ctaLabel, ctaUrl: item.ctaUrl as string }
    : { ...baseProps, ctaLabel: item.ctaLabel }
}

function big3EventToProps(item: Big3EventItem, linkColor: string): Big3EventCardProps {
  const baseProps = {
    title: item.title,
    dateText: item.dateText,
    linkColor,
    ...pickDefined({
      location: item.location,
      compositeImageUrl: item.compositeImageUrl,
    }),
  }
  if (item.ctaLabel === undefined) {
    return baseProps
  }
  const isLinkArm = item.ctaLabel !== 'Save the Date' && item.ctaUrl !== undefined
  return isLinkArm
    ? { ...baseProps, ctaLabel: item.ctaLabel, ctaUrl: item.ctaUrl as string }
    : { ...baseProps, ctaLabel: item.ctaLabel }
}

const BIG3_HEADING = 'Big 3 Meetings'
const SIGNATURE_HEADING = 'Signature Events'

export const big3SignatureEvents = defineBlock({
  id: 'big3-signature-events',
  label: 'Big 3 + Signature Events',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: { big3Events: [], signatureEvents: [] },
  autoHide: ({ inputs }) =>
    inputs.big3Events.length === 0 && inputs.signatureEvents.length === 0,
  render: ({ inputs }, ctx) => {
    // P-037.25 D-133: the FIRST baked subheader ("Big 3 Meetings") drops
    // the divider rule + its top padding so it doesn't render flush
    // against the Block's 30 px top spacer above. The SECOND baked call
    // ("Signature Events") keeps the rule — it visually separates the
    // two arms.
    const big3Heading = subSectionHeader({
      heading: BIG3_HEADING,
      linkColor: ctx.linkColor,
      // P-037.30 ninth post-Phase-Close §C — `noTopRule: true` split
      // into the orthogonal pair `{hideTopRule: true, noTopPad: true}`.
      // Same visible behavior (no rule + no top padding) — the baked
      // "Big 3 Meetings" header sits flush against the Block's
      // internal 20 px top pad. Renaming preserves the byte-exact
      // render shape.
      hideTopRule: true,
      noTopPad: true,
    })
    const signatureHeading = subSectionHeader({
      heading: SIGNATURE_HEADING,
      linkColor: ctx.linkColor,
    })
    const big3Grid = gridLayout(inputs.big3Events, 3, (e) =>
      big3EventCard(big3EventToProps(e, ctx.linkColor)),
    )
    const signatureGrid = gridLayout(inputs.signatureEvents, 3, (e) =>
      eventCard(signatureEventToProps(e, ctx.linkColor)),
    )
    // Auto-hide individual arms when their list is empty — keeps the
    // Block visible (with just the populated arm) when only one is
    // configured. The Block-level autoHide fires only when BOTH are
    // empty.
    //
    // **P-037.29 D-137 §item 2:** SubSectionHeader's internal
    // `PAD_BOTTOM` dropped from 16 → 0 when the system-level inter-
    // Block spacer took ownership of the gap. The standalone
    // sub-section-heading Block gets its 20 px gap from the spacer;
    // baked SubSectionHeader consumers inside a single Block (this
    // one) compensate manually so the heading text doesn't sit flush
    // against the grid below. 20 px matches the standalone Block's
    // spacer-provided gap (sub-section-heading bottom=20).
    // prettier-ignore
    const big3Section = inputs.big3Events.length > 0
      ? html`<tr><td>${big3Heading}</td></tr><tr><td style="padding: 20px 30px 0 30px;">${big3Grid}</td></tr>`
      : undefined
    // prettier-ignore
    const signatureSection = inputs.signatureEvents.length > 0
      ? html`<tr><td>${signatureHeading}</td></tr><tr><td style="padding: 20px 30px 0 30px;">${signatureGrid}</td></tr>`
      : undefined
    // Outer table bg = ctx.tintColor (D-131 item 1). Same 30 px L/R +
    // 30 px top padded rhythm as event-card-group / chapter-sponsors;
    // matching the system 30/190/25/190/25/190/30 3-col rhythm at the
    // gridLayout level.
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" bgcolor="${ctx.tintColor}" style="background-color: ${ctx.tintColor};"><tr><td height="30" style="height: 30px; line-height: 30px; font-size: 30px;">&nbsp;</td></tr>${big3Section}${signatureSection}<tr><td height="30" style="height: 30px; line-height: 30px; font-size: 30px;">&nbsp;</td></tr></table>`
  },
})

// Re-exports for tests
export type { CtaLabel, Big3EventItem, SignatureEventItem }
